/**
 * Helper Utilities
 * Shared utility functions
 */

/**
 * Debounce function calls
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Throttle function calls
 */
export function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * Check if element is in viewport
 */
export function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

/**
 * Scroll to element smoothly
 */
export function scrollToElement(element, offset = 0) {
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
    });
}

/**
 * Format price
 */
export function formatPrice(amount, currency = '$') {
    return `${currency}${amount.toLocaleString()}`;
}

/**
 * Generate unique ID
 */
export function generateId(prefix = 'id') {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Parse query string
 */
export function parseQueryString(url = window.location.href) {
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const params = {};
    const pairs = queryString.split('&');

    pairs.forEach(pair => {
        const [key, value] = pair.split('=');
        params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });

    return params;
}

/**
 * Get the region-specific flag sprite path.
 * Reads the site region from .site-title-region element and maps to the correct sprite file.
 * Falls back to the global sprite if region can't be determined.
 */
let _flagSpritePath = null;
export function getFlagSpritePath() {
    if (_flagSpritePath) return _flagSpritePath;
    const region = document.querySelector('.site-title-region')?.textContent?.trim().toLowerCase() || '';
    const map = {
        'africa': 'sprite-flags-africa.svg',
        'america': 'sprite-flags-america.svg',
        'europe': 'sprite-flags-europe.svg',
        'hong kong': 'sprite-flags-hong-kong.svg',
        'asia': 'sprite-flags-asia.svg',
    };
    const file = map[region] || 'sprite-flags.svg';
    _flagSpritePath = `/img/${file}?v=2`;
    return _flagSpritePath;
}

/**
 * Get the default ISO country code for the current site region.
 */
export function getDefaultIso() {
    const region = document.querySelector('.site-title-region')?.textContent?.trim().toLowerCase() || '';
    return { africa: 'za', america: 'us', europe: 'gb', asia: 'sg', 'hong kong': 'hk' }[region] || 'us';
}

/**
 * Add a search/filter input to a dropdown menu.
 * Filters child elements matching `optionSelector` by their text content.
 *
 * @param {HTMLElement} menu - The menu container to prepend the search input into
 * @param {Object} [options]
 * @param {string} [options.placeholder='Search...'] - Placeholder text
 * @param {string} [options.optionSelector='.country-select-option'] - Selector for filterable items
 * @param {string} [options.separatorSelector='.country-select-separator'] - Selector for separator elements to hide during search
 * @param {Function} [options.getSearchText] - Custom function(el) returning the searchable string; defaults to dataset.searchText || textContent
 */
export function addDropdownSearch(menu, options = {}) {
    const {
        placeholder = 'Search...',
        optionSelector = '.country-select-option',
        separatorSelector = '.country-select-separator',
        getSearchText = el => (el.dataset.searchText || el.textContent).toLowerCase(),
        onSelect = el => el.click(),
        onClose = null,
        wrapClassName = 'dropdown-search',
        inputClassName = 'dropdown-search-input',
    } = options;

    const searchWrap = document.createElement('div');
    searchWrap.className = wrapClassName;
    const input = document.createElement('input');
    input.type = 'text';
    input.className = inputClassName;
    input.placeholder = placeholder;
    input.autocomplete = 'off';
    searchWrap.appendChild(input);
    menu.prepend(searchWrap);

    let activeOption = null;

    const getOptions = () => [...menu.querySelectorAll(optionSelector)];
    const getVisibleOptions = () => getOptions().filter(item => !item.hidden);
    const setActiveOption = (option) => {
        activeOption = option || null;

        getOptions().forEach(item => {
            item.classList.toggle('is-active', item === activeOption);
        });

        if (activeOption) {
            activeOption.scrollIntoView({ block: 'nearest' });
        }
    };

    input.addEventListener('input', () => {
        const query = input.value.toLowerCase().trim();
        getOptions().forEach(item => {
            item.hidden = query ? !getSearchText(item).includes(query) : false;
        });
        menu.querySelectorAll(separatorSelector).forEach(sep => {
            sep.hidden = query.length > 0;
        });

        const visibleOptions = getVisibleOptions();
        if (!visibleOptions.length) {
            setActiveOption(null);
            return;
        }

        if (!activeOption || activeOption.hidden) {
            setActiveOption(visibleOptions[0]);
        }
    });

    input.addEventListener('keydown', e => {
        e.stopPropagation();

        const visibleOptions = getVisibleOptions();

        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && visibleOptions.length) {
            e.preventDefault();

            const direction = e.key === 'ArrowDown' ? 1 : -1;
            const currentIndex = activeOption ? visibleOptions.indexOf(activeOption) : -1;
            const nextIndex = currentIndex === -1
                ? (direction > 0 ? 0 : visibleOptions.length - 1)
                : Math.min(Math.max(currentIndex + direction, 0), visibleOptions.length - 1);

            setActiveOption(visibleOptions[nextIndex]);
            return;
        }

        if ((e.key === 'Home' || e.key === 'PageUp') && visibleOptions.length) {
            e.preventDefault();
            setActiveOption(visibleOptions[0]);
            return;
        }

        if ((e.key === 'End' || e.key === 'PageDown') && visibleOptions.length) {
            e.preventDefault();
            setActiveOption(visibleOptions[visibleOptions.length - 1]);
            return;
        }

        if (e.key === 'Enter') {
            const option = activeOption || visibleOptions[0];
            if (option) {
                e.preventDefault();
                onSelect(option);
            }
            return;
        }

        if (e.key === 'Escape' && typeof onClose === 'function') {
            e.preventDefault();
            onClose();
        }
    });

    // Public API: reset the search when menu closes
    return {
        reset() {
            input.value = '';
            input.dispatchEvent(new Event('input'));
        },
        focus() {
            input.focus();
        },
        getVisibleOptions,
        setActiveOption,
        clearActive() {
            setActiveOption(null);
        },
        input,
    };
}

/**
 * Get a cookie value by name
 */
export function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Set a cookie
 */
export function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}

/**
 * Update URL without page reload
 */
export function updateUrl(params, replaceState = false) {
    const url = new URL(window.location.href);

    Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === '') {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, params[key]);
        }
    });

    if (replaceState) {
        window.history.replaceState({}, '', url);
    } else {
        window.history.pushState({}, '', url);
    }
}
